/**
 * SegmentProfitabilityTable Component
 *
 * Displays a sortable table of segment-level profitability data.
 * Columns: Segment, Revenue, kg Sold, Variable Cost, Contribution, Margin %
 *
 * Part of US-P7-002 Step 6: UI Components
 */

import { useState, useMemo } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { ArrowUpDown, ArrowUp, ArrowDown, Users, Building2, Warehouse, HelpCircle, Info } from 'lucide-react'
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip'
import MarginBadge from './MarginBadge'

/**
 * Format a currency value in British pounds
 *
 * @param {number|null} value - Value to format
 * @returns {string} Formatted currency string
 */
function formatCurrency(value) {
  if (value === null || value === undefined) return '-'
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value)
}

/**
 * Format a kg value with two decimal places
 *
 * @param {number|null} value - Value to format
 * @returns {string} Formatted kg string
 */
function formatKg(value) {
  if (value === null || value === undefined) return '-'
  return value.toFixed(2)
}

/**
 * Get the segment badge style and icon
 *
 * @param {string} segmentName - Name of the segment
 * @returns {Object} Icon component and colour classes
 */
function getSegmentStyle(segmentName) {
  switch (segmentName) {
    case 'Retail':
      return {
        Icon: Users,
        bgClass: 'bg-blue-100',
        textClass: 'text-blue-700'
      }
    case 'Commercial':
      return {
        Icon: Building2,
        bgClass: 'bg-purple-100',
        textClass: 'text-purple-700'
      }
    case 'Wholesale':
      return {
        Icon: Warehouse,
        bgClass: 'bg-orange-100',
        textClass: 'text-orange-700'
      }
    default:
      return {
        Icon: HelpCircle,
        bgClass: 'bg-gray-100',
        textClass: 'text-gray-700'
      }
  }
}

/**
 * Segment badge component
 */
function SegmentBadge({ name }) {
  const { Icon, bgClass, textClass } = getSegmentStyle(name)

  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-sm font-medium ${bgClass} ${textClass}`}>
      <Icon className="h-4 w-4" />
      {name}
    </span>
  )
}

/**
 * Column definitions for the table
 */
const columns = [
  { key: 'segment_name', label: 'Segment', sortable: true },
  { key: 'revenue', label: 'Revenue', sortable: true, tip: 'Total sales income for this customer segment' },
  { key: 'kg_sold', label: 'kg Sold', sortable: true, tip: 'Total weight sold in kilograms' },
  { key: 'variable_cost', label: 'Variable Cost', sortable: true, tip: 'Total production costs (substrate, spawn, energy) for the kg sold to this segment' },
  { key: 'contribution', label: 'Contribution', sortable: true, tip: 'Revenue minus variable costs. The amount this segment contributes toward covering fixed costs' },
  { key: 'margin_percent', label: 'Margin %', sortable: true, tip: 'Contribution as a percentage of revenue' },
]

/**
 * Sort data by column
 *
 * @param {Array} data - Data to sort
 * @param {string} sortKey - Column key to sort by
 * @param {string} sortDirection - 'asc' or 'desc'
 * @returns {Array} Sorted data
 */
function sortData(data, sortKey, sortDirection) {
  return [...data].sort((a, b) => {
    const aVal = a[sortKey]
    const bVal = b[sortKey]

    // Handle null values - sort them to the end
    if (aVal === null || aVal === undefined) return 1
    if (bVal === null || bVal === undefined) return -1

    // String comparison for segment_name
    if (sortKey === 'segment_name') {
      const comparison = aVal.localeCompare(bVal)
      return sortDirection === 'asc' ? comparison : -comparison
    }

    // Numeric comparison for other columns
    const comparison = aVal - bVal
    return sortDirection === 'asc' ? comparison : -comparison
  })
}

/**
 * SegmentProfitabilityTable Component
 *
 * @param {Object} props
 * @param {Array} props.data - Array of segment profitability data
 * @param {boolean} [props.loading=false] - Loading state
 * @param {string} [props.className] - Additional CSS classes
 */
export default function SegmentProfitabilityTable({
  data,
  loading = false,
  className = ''
}) {
  // Default sort by revenue descending
  const [sortKey, setSortKey] = useState('revenue')
  const [sortDirection, setSortDirection] = useState('desc')

  // Sort the data
  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return []
    return sortData(data, sortKey, sortDirection)
  }, [data, sortKey, sortDirection])

  /**
   * Handle column header click for sorting
   */
  const handleSort = (key) => {
    if (sortKey === key) {
      // Toggle direction if same column
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      // New column, default to descending
      setSortKey(key)
      setSortDirection('desc')
    }
  }

  /**
   * Get the sort icon for a column
   */
  const getSortIcon = (key) => {
    if (sortKey !== key) {
      return <ArrowUpDown className="h-4 w-4 ml-1" />
    }
    return sortDirection === 'asc'
      ? <ArrowUp className="h-4 w-4 ml-1" />
      : <ArrowDown className="h-4 w-4 ml-1" />
  }

  // Loading state
  if (loading) {
    return (
      <div className={className}>
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div className={className}>
        <div className="text-center py-8 text-neutral-500">
          No segment profitability data available for this period
        </div>
      </div>
    )
  }

  return (
    <TooltipProvider delayDuration={300}>
    <div className={className}>
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((column) => (
              <TableHead key={column.key}>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => handleSort(column.key)}
                    className="flex items-center hover:text-neutral-900 transition-colors"
                  >
                    {column.label}
                    {getSortIcon(column.key)}
                  </button>
                  {column.tip && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-3.5 w-3.5 text-muted-foreground/50 hover:text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs text-xs">
                        {column.tip}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedData.map((row, index) => (
            <TableRow key={row.segment_name || index}>
              <TableCell>
                <SegmentBadge name={row.segment_name} />
              </TableCell>
              <TableCell>{formatCurrency(row.revenue)}</TableCell>
              <TableCell>{formatKg(row.kg_sold)}</TableCell>
              <TableCell>{formatCurrency(row.variable_cost)}</TableCell>
              <TableCell>{formatCurrency(row.contribution)}</TableCell>
              <TableCell>
                <MarginBadge margin={row.margin_percent} size="compact" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
    </TooltipProvider>
  )
}
